import React from "react";
import styled, { keyframes } from "styled-components";
import { FaBug, FaLightbulb, FaClock, FaTrophy, FaCode } from "react-icons/fa";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

// Full-page wrapper
const PageWrapper = styled.div`
  background: linear-gradient(to bottom, rgb(16, 16, 16), rgb(53, 53, 53));
  color: #fff;
  min-height: 100vh;
  padding: 80px 20px;
  text-align: center;
`;

// Logo
const Logo = styled.img`
  width: 120px;
  margin-bottom: 20px;
  animation: ${fadeIn} 1s ease forwards;

  @media (max-width: 768px) {
    width: 90px;
  }
`;

// Headline
const Headline = styled.h1`
  font-size: 48px;
  color: #a855f7;
  margin-bottom: 20px;
  animation: ${fadeIn} 1.1s ease forwards;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

// Subtitle
const Subtitle = styled.p`
  font-size: 20px;
  color: #ccc;
  margin-bottom: 60px;
  animation: ${fadeIn} 1.2s ease forwards;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

// Section Title
const SectionTitle = styled.h2`
  font-size: 32px;
  color: #a855f7;
  margin-bottom: 40px;
  animation: ${fadeIn} 1.3s ease forwards;
`;

// Cards container
const CardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  margin-bottom: 80px;
`;

// Individual card
const Card = styled.div`
  background: #1f1f1f;
  border-radius: 16px;
  padding: 30px 25px;
  width: 250px;
  text-align: center;
  transition: transform 0.3s, box-shadow 0.3s;
  animation: ${fadeIn} 1.5s ease forwards;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(168, 85, 247, 0.5);
  }
`;

// Icon style
const CardIcon = styled.div`
  font-size: 40px;
  color: #a855f7;
  margin-bottom: 15px;
  animation: ${float} 3s ease-in-out infinite;
`;

const CardTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 10px;
`;

const CardDesc = styled.p`
  font-size: 14px;
  color: #ccc;
`;

const LandingPage = () => {
  const problems = [
    {
      icon: <FaBug />,
      title: "Confusing Errors",
      desc: "Get quick explanations for confusing coding errors.",
    },
    {
      icon: <FaLightbulb />,
      title: "Lack of Guidance",
      desc: "Receive expert advice and solutions from top developers.",
    },
    {
      icon: <FaClock />,
      title: "Slow Problem Solving",
      desc: "Find answers faster without wasting hours searching online.",
    },
    {
      icon: <FaTrophy />,
      title: "No Rewards",
      desc: "Earn coins and rankings as you solve or post issues.",
    },
  ];

  const offers = [
    {
      icon: <FaLightbulb />,
      title: "Post Your Issues",
      desc: "Describe your coding problem and submit it easily.",
    },
    {
      icon: <FaCode />,
      title: "Get Solutions",
      desc: "Receive solutions from top developers worldwide.",
    },
    {
      icon: <FaTrophy />,
      title: "Earn Rewards",
      desc: "Rank up, earn coins and rewards for contributions.",
    },
  ];

  return (
    <PageWrapper>
      {/* <Logo src={LogoImg} alt="Logo" /> */}
      <Headline>🚀 SolveMyCodeIssue is Launching Soon!</Headline>
      <Subtitle>
        Post coding issues, get expert solutions, earn rewards, and rank up.
      </Subtitle>

      {/* Problems We Solve */}
      <SectionTitle>Problems We Face as Developers</SectionTitle>
      <CardsContainer>
        {problems.map((p, idx) => (
          <Card key={idx}>
            <CardIcon>{p.icon}</CardIcon>
            <CardTitle>{p.title}</CardTitle>
            <CardDesc>{p.desc}</CardDesc>
          </Card>
        ))}
      </CardsContainer>

      {/* What We Offer */}
      <SectionTitle>What We Offer on this Platform</SectionTitle>
      <CardsContainer>
        {offers.map((o, idx) => (
          <Card key={idx}>
            <CardIcon>{o.icon}</CardIcon>
            <CardTitle>{o.title}</CardTitle>
            <CardDesc>{o.desc}</CardDesc>
          </Card>
        ))}
      </CardsContainer>
    </PageWrapper>
  );
};

export default LandingPage;
